/**
 * Type guard helpers for mathematics experiments
 *
 * These helpers provide type-safe access to Record<string, number> properties
 */
export const getParam = (params, key) => {
    const val = params[key];
    if (val === undefined)
        throw new Error(`Missing parameter: ${key}`);
    return val;
};
export const getState = (state, key) => {
    const val = state[key];
    if (val === undefined)
        throw new Error(`Missing state variable: ${key}`);
    return val;
};
/**
 * Extract the variables Record from a SimulationState for use in failureMode checks
 */
export const getStateValues = (state) => {
    return state.variables;
};
/**
 * Check if a number is prime
 */
export const isPrime = (n) => {
    if (n < 2)
        return false;
    if (n === 2)
        return true;
    if (n % 2 === 0)
        return false;
    for (let i = 3; i <= Math.sqrt(n); i += 2) {
        if (n % i === 0)
            return false;
    }
    return true;
};
/**
 * Count prime factors of a number
 */
export const countPrimeFactors = (n) => {
    let count = 0;
    let num = Math.abs(n);
    while (num % 2 === 0) {
        count++;
        num /= 2;
    }
    for (let i = 3; i <= Math.sqrt(num); i += 2) {
        while (num % i === 0) {
            count++;
            num /= i;
        }
    }
    if (num > 2)
        count++;
    return count;
};
/**
 * Compute GCD using Euclidean algorithm
 */
export const gcd = (a, b) => {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b) {
        const t = b;
        b = a % b;
        a = t;
    }
    return a;
};
/**
 * Compute LCM
 */
export const lcm = (a, b) => {
    return Math.abs(a * b) / gcd(a, b);
};
/**
 * Convert decimal to fraction approximation
 */
export const decimalToFraction = (decimal, maxDenominator = 1000) => {
    let n0 = Math.floor(decimal);
    let frac = decimal - n0;
    if (frac < 1e-10)
        return { numerator: n0, denominator: 1 };
    let a = 0;
    let b = 1;
    let c = 1;
    let d = 1;
    while (b <= maxDenominator && d <= maxDenominator) {
        const mediant = (a + c) / (b + d);
        if (Math.abs(frac - mediant) < 1e-10) {
            if (b + d <= maxDenominator) {
                return { numerator: n0 * (b + d) + a + c, denominator: b + d };
            }
            if (d > b)
                return { numerator: n0 * d + c, denominator: d };
            return { numerator: n0 * b + a, denominator: b };
        }
        if (frac > mediant) {
            a += c;
            b += d;
        }
        else {
            c += a;
            d += b;
        }
    }
    if (b > maxDenominator)
        return { numerator: n0 * d + c, denominator: d };
    return { numerator: n0 * b + a, denominator: b };
};
/**
 * Compute factorial
 */
export const factorial = (n) => {
    if (n < 0)
        return NaN;
    if (n === 0 || n === 1)
        return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
        result *= i;
    }
    return result;
};
/**
 * Compute binomial coefficient
 */
export const binomial = (n, k) => {
    if (k < 0 || k > n)
        return 0;
    if (k === 0 || k === n)
        return 1;
    if (k > n / 2)
        k = n - k;
    let result = 1;
    for (let i = 0; i < k; i++) {
        result *= (n - i) / (i + 1);
    }
    return result;
};
/**
 * Compute sum of arithmetic sequence
 */
export const arithmeticSum = (a1, d, n) => {
    return (n / 2) * (2 * a1 + (n - 1) * d);
};
/**
 * Compute sum of geometric sequence
 */
export const geometricSum = (a1, r, n) => {
    if (Math.abs(r - 1) < 1e-10)
        return a1 * n;
    return a1 * (1 - Math.pow(r, n)) / (1 - r);
};
/**
 * Matrix multiplication for 2x2 matrices
 */
export const multiplyMatrices2x2 = (a, b) => {
    return [
        [a[0][0] * b[0][0] + a[0][1] * b[1][0], a[0][0] * b[0][1] + a[0][1] * b[1][1]],
        [a[1][0] * b[0][0] + a[1][1] * b[1][0], a[1][0] * b[0][1] + a[1][1] * b[1][1]],
    ];
};
/**
 * Determinant of 2x2 matrix
 */
export const det2x2 = (m) => {
    return m[0][0] * m[1][1] - m[0][1] * m[1][0];
};
/**
 * Convert between bases
 */
export const convertBase = (num, fromBase, toBase) => {
    if (fromBase < 2 || fromBase > 36 || toBase < 2 || toBase > 36) {
        throw new Error('Base must be between 2 and 36');
    }
    // Convert to decimal first
    const decimal = parseInt(num.toString(), fromBase);
    // Then to target base
    return decimal.toString(toBase).toUpperCase();
};
/**
 * Fibonacci number
 */
export const fibonacci = (n) => {
    if (n <= 0)
        return 0;
    if (n === 1 || n === 2)
        return 1;
    let a = 1;
    let b = 1;
    for (let i = 3; i <= n; i++) {
        const temp = a + b;
        a = b;
        b = temp;
    }
    return b;
};
/**
 * Stirling numbers of the second kind
 */
export const stirling2 = (n, k) => {
    if (k > n || k < 1)
        return 0;
    if (k === 1 || k === n)
        return 1;
    return k * stirling2(n - 1, k) + stirling2(n - 1, k - 1);
};
/**
 * Check if graph has cycle (adjacency matrix)
 */
export const hasCycle = (adjMatrix) => {
    const n = adjMatrix.length;
    const visited = new Set();
    const recStack = new Set();
    const dfs = (v) => {
        visited.add(v);
        recStack.add(v);
        const row = adjMatrix[v];
        if (row) {
            for (let u = 0; u < n; u++) {
                const val = row?.[u];
                if (val !== undefined && val > 0) {
                    if (!visited.has(u)) {
                        if (dfs(u))
                            return true;
                    }
                    else if (recStack.has(u)) {
                        return true;
                    }
                }
            }
        }
        recStack.delete(v);
        return false;
    };
    for (let i = 0; i < n; i++) {
        if (!visited.has(i)) {
            if (dfs(i))
                return true;
        }
    }
    return false;
};
//# sourceMappingURL=helpers.js.map